import {
  Alert,
  BottomSheet,
  Button,
  Card,
  Field,
  FieldHint,
  Input,
  Label,
  StatusPill,
  cn,
} from "@breenova/ui";
import { CalendarCheck, Lock, LockOpen, Pencil, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useLocation } from "react-router";

import { DetailHeader } from "@/components/app-shell";
import { DateField } from "@/components/date-field";
import {
  seasonLabel,
  useBreedingSeasons,
  useCreateBreedingSeason,
  useUpdateBreedingSeason,
  type BreedingSeasonDto,
} from "@/lib/breeding";

/**
 * Where a breeder opens, closes and switches breeding seasons.
 *
 * The season is the record everything else in breeding hangs off — a pair belongs to one,
 * a round numbers from one within it — and it was the only such record with no way to
 * create it. The endpoints existed; a new breeder simply met "No breeding season is open"
 * on the first pair they tried to record, with nothing in the app that could open one.
 *
 * Not filed under "Your aviary" with the cages: a cage is furniture that outlives every
 * season, while the season is the year being worked. It belongs beside the pairs it
 * governs, which is where the breeder is standing when the year turns.
 */
export function SeasonsPage() {
  const seasons = useBreedingSeasons();

  // Reached from the nests screen and from the More hub. The back arrow goes wherever the
  // breeder came from; the nests screen is the fallback for a bookmark or a reload, since
  // that is the screen this one exists to serve.
  const { state } = useLocation() as { state?: { from?: string } };

  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = seasons.data?.seasons ?? [];

  return (
    <div className="flex flex-col gap-md">
      <DetailHeader
        backTo={state?.from ?? "/breeding"}
        kicker="Breeding"
        title="Seasons"
        action={
          <Button
            size="sm"
            onClick={() => {
              setError(null);
              setSheet({ mode: "create" });
            }}
          >
            <Plus className="size-4" aria-hidden />
            New season
          </Button>
        }
      />

      <section className="flex flex-col gap-md px-5 pb-lg md:px-gutter-wide" aria-live="polite">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {seasons.isPending ? (
          <p className="py-xl text-center text-body text-on-surface-variant">
            Loading your seasons…
          </p>
        ) : seasons.isError ? (
          <Alert tone="error">Could not load your breeding seasons.</Alert>
        ) : rows.length === 0 ? (
          <Card className="flex flex-col items-center gap-sm p-xl text-center">
            <p className="text-card-title text-on-surface">No season is open</p>
            <p className="max-w-prose text-body text-on-surface-variant">
              A pair and a nest both belong to a season, so nothing in breeding can be
              recorded until one exists. Open this year and it becomes the season new
              records default into.
            </p>
            <Button className="mt-sm" onClick={() => setSheet({ mode: "create" })}>
              <Plus className="size-4" aria-hidden />
              Open a season
            </Button>
          </Card>
        ) : (
          <ul className="grid grid-cols-1 gap-md lg:grid-cols-2 lg:items-start">
            {rows.map((season) => (
              <li key={season.id}>
                <SeasonCard
                  season={season}
                  onEdit={() => {
                    setError(null);
                    setSheet({ mode: "edit", season });
                  }}
                  onError={setError}
                />
              </li>
            ))}
          </ul>
        )}

        <Card tone="recessed" flat className="flex flex-col gap-sm p-lg">
          <h2 className="text-label-caps uppercase text-on-surface-variant">
            How a season works
          </h2>
          <p className="text-detail text-on-surface-variant">
            A year holds as many seasons as you run. Birds kept inside in a heated birdroom
            go down in January and the outdoor birds in April, and the two do not perform
            alike — so they are two seasons, each with its own rates, told apart by a name.
          </p>
          <p className="text-detail text-on-surface-variant">
            Exactly one of them is current. A new pair or round goes into it without being
            asked, which is what makes recording a clutch on a phone a single tap. Switch
            the current one when the next programme goes down; the pairs already in the
            first one keep producing rounds there.
          </p>
          <p className="text-detail text-on-surface-variant">
            Closing a season makes it read-only, so last year&rsquo;s results cannot drift
            once you have stopped working it. A closed season cannot also be the current
            one. Reopen it if something needs correcting.
          </p>
        </Card>
      </section>

      {sheet ? (
        <SeasonSheet
          state={sheet}
          years={rows.map((season) => season.year)}
          onClose={() => setSheet(null)}
          onError={setError}
        />
      ) : null}
    </div>
  );
}

/**
 * One season.
 *
 * The two changes a breeder actually makes mid-year — switch which season is current,
 * close the one that is finished — are on the card rather than inside the edit sheet.
 * Both are a single field on the server, and burying a one-tap action behind a form that
 * also asks for a name and two dates would be asking four questions to answer one.
 */
function SeasonCard({
  season,
  onEdit,
  onError,
}: {
  season: BreedingSeasonDto;
  onEdit: () => void;
  onError: (message: string | null) => void;
}) {
  const update = useUpdateBreedingSeason();

  async function set(change: { isCurrent?: boolean; isClosed?: boolean }): Promise<void> {
    try {
      await update.mutateAsync({
        id: season.id,
        body: {
          id: season.id,
          // Null on everything not being changed: the handler merges, so null is what
          // "leave this alone" looks like on the wire.
          name: null,
          startsOn: null,
          endsOn: null,
          isCurrent: change.isCurrent ?? null,
          isClosed: change.isClosed ?? null,
        },
      });

      onError(null);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "That could not be saved.");
    }
  }

  return (
    <Card className="flex flex-col gap-md p-lg">
      <div className="flex items-start gap-md">
        <div className="flex min-w-0 flex-1 flex-col gap-hair">
          <span className="flex flex-wrap items-center gap-sm">
            <span className="text-section-header text-on-surface" data-numeric>
              {seasonLabel(season)}
            </span>

            {season.isCurrent ? <StatusPill tone="positive">Current</StatusPill> : null}
            {season.isClosed ? <StatusPill tone="neutral">Closed</StatusPill> : null}
          </span>

          <span className="truncate text-caption text-on-surface-variant" data-numeric>
            {dateRange(season) ?? "No dates set"}
          </span>
        </div>

        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit the ${seasonLabel(season)} season`}
          className={cn(
            "flex size-touch shrink-0 items-center justify-center rounded-full",
            "text-on-surface-variant transition-colors hover:bg-surface-container",
          )}
        >
          <Pencil className="size-4.5" aria-hidden />
        </button>
      </div>

      <dl className="flex gap-xl">
        <div className="flex flex-col gap-hair">
          <dt className="text-tile-label uppercase text-on-surface-variant">Pairs</dt>
          <dd className="text-data text-on-surface" data-numeric>
            {season.pairCount}
          </dd>
        </div>

        <div className="flex flex-col gap-hair">
          <dt className="text-tile-label uppercase text-on-surface-variant">Rounds</dt>
          <dd className="text-data text-on-surface" data-numeric>
            {season.nestCount}
          </dd>
        </div>
      </dl>

      <div className="flex flex-wrap gap-sm">
        {/* A closed season cannot be current — the server clears the one flag when the
            other is set, so offering both at once would offer a contradiction. */}
        {!season.isCurrent && !season.isClosed ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={update.isPending}
            onClick={() => void set({ isCurrent: true })}
          >
            <CalendarCheck className="size-4" aria-hidden />
            Make current
          </Button>
        ) : null}

        {season.isClosed ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={update.isPending}
            onClick={() => void set({ isClosed: false })}
          >
            <LockOpen className="size-4" aria-hidden />
            Reopen
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            disabled={update.isPending}
            onClick={() => void set({ isClosed: true })}
          >
            <Lock className="size-4" aria-hidden />
            Close
          </Button>
        )}
      </div>
    </Card>
  );
}

type SheetState = { mode: "create" } | { mode: "edit"; season: BreedingSeasonDto };

/** Open or edit, in one sheet. */
function SeasonSheet({
  state,
  years,
  onClose,
  onError,
}: {
  state: SheetState;
  years: readonly number[];
  onClose: () => void;
  onError: (message: string | null) => void;
}) {
  const create = useCreateBreedingSeason();
  const update = useUpdateBreedingSeason();

  const editing = state.mode === "edit" ? state.season : undefined;

  const [year, setYear] = useState(() => String(editing?.year ?? thisYear()));
  const [name, setName] = useState(editing?.name ?? "");
  const [startsOn, setStartsOn] = useState(editing?.startsOn ?? "");
  const [endsOn, setEndsOn] = useState(editing?.endsOn ?? "");
  const [makeCurrent, setMakeCurrent] = useState(editing ? editing.isCurrent : true);
  const [message, setMessage] = useState<string | null>(null);

  const busy = create.isPending || update.isPending;

  // The year already has a season, so this one has nothing else to be known by. The
  // server refuses it too; saying so while the field is on screen is the point.
  const needsName = !editing && years.includes(Number(year.trim()));

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);

    const parsed = Number(year.trim());

    // The same window the server's validator uses. Saying it here means a mistyped 202
    // comes back as a sentence about years rather than as a validation problem.
    if (!Number.isInteger(parsed) || parsed < 1950 || parsed > 2100) {
      setMessage("A season is a year between 1950 and 2100.");
      return;
    }

    if (startsOn && endsOn && endsOn < startsOn) {
      setMessage("A season cannot end before it starts.");
      return;
    }

    if (needsName && !name.trim()) {
      setMessage(
        `${parsed} already has a season. Give this one a name — “indoor” and “outdoor”, ` +
          "say — so the two can be told apart.",
      );
      return;
    }

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            id: editing.id,
            name: name.trim() || null,
            startsOn: startsOn || null,
            endsOn: endsOn || null,
            // Only ever sent as true: false would be a season standing itself down with
            // nothing standing up, and the breeder always has exactly one current.
            isCurrent: makeCurrent && !editing.isCurrent ? true : null,
            isClosed: null,
          },
        });
      } else {
        await create.mutateAsync({
          year: parsed,
          name: name.trim() || null,
          startsOn: startsOn || null,
          endsOn: endsOn || null,
          makeCurrent,
        });
      }

      onError(null);
      onClose();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "That could not be saved.");
    }
  }

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={editing ? `The ${seasonLabel(editing)} season` : "Open a season"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>

          {/*
            * "Open", not "Create": the season's own lifecycle is open and closed — the
            * cards behind this sheet carry Close and Reopen, and `EnsureOpen` is what
            * refuses a write against a finished season.
            *
            * The header action says "New season" instead, because there the word is not
            * describing the act. A bare "Open" above a list of seasons reads as "open the
            * one I picked", which is the one thing that button does not do.
            */}
          <Button type="submit" form="season-form" className="flex-1" disabled={busy}>
            {busy ? "Saving…" : editing ? "Save" : "Open season"}
          </Button>
        </>
      }
    >
      <form
        id="season-form"
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-lg"
      >
        {message ? <Alert tone="error">{message}</Alert> : null}

        {/* The year is what the season IS: every pair, round and ring already filed under
            it would be filed under a different year afterwards. The server does not accept
            a change either, so the field is not offered once the season exists. */}
        {editing ? null : (
          <Field>
            <Label htmlFor="season-year">Year</Label>
            <Input
              id="season-year"
              value={year}
              onChange={(event) => setYear(event.target.value)}
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
            />
            <FieldHint>
              {years.includes(Number(year.trim()))
                ? `${year.trim()} already has a season, so this one needs a name below.`
                : years.length === 0
                  ? "The year this programme belongs to. This is your first season."
                  : `You already have ${listYears(years)}.`}
            </FieldHint>
          </Field>
        )}

        <Field>
          <Label htmlFor="season-name">Name</Label>
          <Input
            id="season-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={needsName ? "indoor, outdoor…" : "Optional"}
            autoComplete="off"
            maxLength={200}
          />
          <FieldHint>
            {needsName
              ? "Two seasons in one year have to be told apart, and this is the only thing a picker can show."
              : "Only needed once a year holds more than one season — “indoor” and “outdoor”, say."}
          </FieldHint>
        </Field>

        {/*
         * Both dates are a note to yourself: nothing filters or warns off them, and a
         * round recorded outside them is still in the season. They are here because a
         * heated birdroom and the southern hemisphere do not run to the calendar year.
         *
         * Not clearable: the update merges, so a blanked date arrives as null and the
         * server reads null as "leave it alone". A Clear button that silently did nothing
         * would be worse than not offering one.
         */}
        <Field>
          <Label>Starts on</Label>
          <DateField
            label="Season starts on"
            value={startsOn}
            onChange={setStartsOn}
            clearable={false}
            placeholder="Optional"
          />
        </Field>

        <Field>
          <Label>Ends on</Label>
          <DateField
            label="Season ends on"
            value={endsOn}
            onChange={setEndsOn}
            clearable={false}
            placeholder="Optional"
          />
          <FieldHint>
            Both dates are for your own reference. A season holds whatever was recorded
            into it, not whatever falls between these two days.
          </FieldHint>
        </Field>

        {/* Already current, or closed: in both cases there is nothing to offer. A closed
            season is refused the flag, and the one that is already current cannot stand
            itself down without leaving the breeder with no current season at all. */}
        {editing?.isCurrent || editing?.isClosed ? null : (
          <label
            className={cn(
              "flex min-h-touch items-center justify-between gap-md rounded-inset px-lg py-md",
              "bg-surface-container-low",
            )}
          >
            <span className="flex flex-col gap-hair">
              <span className="text-label text-on-surface">Make this the current season</span>
              <span className="text-caption text-on-surface-variant">
                New pairs and rounds go into it without being asked. Whichever season is
                current now steps aside.
              </span>
            </span>

            <input
              type="checkbox"
              checked={makeCurrent}
              onChange={(event) => setMakeCurrent(event.target.checked)}
              className="size-5 shrink-0 accent-[var(--color-primary)]"
            />
          </label>
        )}
      </form>
    </BottomSheet>
  );
}

/** `1 Feb – 31 Jul`, or whichever end of it was given, or nothing at all. */
function dateRange(season: BreedingSeasonDto): string | null {
  const from = season.startsOn ? formatDay(season.startsOn) : null;
  const to = season.endsOn ? formatDay(season.endsOn) : null;

  if (from && to) {
    return `${from} – ${to}`;
  }

  return from ? `from ${from}` : to ? `until ${to}` : null;
}

/** Read as UTC and printed as UTC: a season date is a calendar day, not an instant. */
function formatDay(value: string): string {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/**
 * The year to offer for a new season: this one.
 *
 * It used to skip to the next free year, back when a year held one season. It no longer
 * does — a breeder opening their outdoor season in April is opening a second season in
 * the year they are standing in, and being moved to next year would be wrong every time.
 */
function thisYear(): number {
  return new Date().getFullYear();
}

/**
 * "2024, 2025 and 2026", oldest first, so the hint reads as a sentence.
 *
 * Deduplicated: a year can now carry several seasons, and "2026, 2026 and 2025" is not a
 * sentence anybody wrote on purpose.
 */
function listYears(years: readonly number[]): string {
  const sorted = [...new Set(years)].sort((a, b) => a - b).map(String);

  if (sorted.length === 1) {
    return sorted[0]!;
  }

  return `${sorted.slice(0, -1).join(", ")} and ${sorted.at(-1)}`;
}
